import api from './api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from 'packages/logger';
import utils from './utils';
import {
    selectInvoiceCheckout,
    selectInvoiceOngoingPayment,
    selectInvoicePayment,
    selectSellerPhoneNumber
} from './selector';
import {
    closePaymentModal,
    openPaymentModal,
    resetOngoingPaymentAttempt,
    setCheckoutSummaryFetching,
    setPaymentMethodData
} from './reducer';
import { CHECKOUT_ERRORS } from 'tradera/helpers/checkout-errors';
import {
    MODAL_TYPE,
    PAYMENT_OPTIONS
} from 'tradera/state/views/invoice-checkout/initial-state';
import { paymentInstance } from 'tradera/apps/my-tradera/invoice-checkout/payment/braintree-drop-in';
import {
    selectMemberEmail,
    selectMemberFirstName,
    selectMemberLastName
} from 'tradera/state/member/selectors';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';

export const getInvoice = createAsyncThunk(
    'invoiceCheckout/getInvoice',
    async ({ invoiceId }, thunkAPI) => {
        try {
            const result = await api.getInvoice(invoiceId);

            return result.data;
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

// Payment

export const getPaymentOptions = createAsyncThunk(
    'invoiceCheckout/getPaymentOptions',
    async (invoiceId, thunkAPI) => {
        try {
            const { data } = await api.getPaymentOptions(invoiceId);
            return data;
        } catch (error) {
            logger(error);
            return thunkAPI.rejectWithValue(error);
        }
    }
);

const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

const PAYMENT_STATUS = {
    AUTHORIZED: 'Authorized',
    AWAITING_SWISH: 'AwaitingExternalAuthentication',
    CANCELLED: 'Cancelled',
    CANCELLING: 'Cancelling',
    CANCELLING_FAILED: 'CancellingFailed',
    COMPLETING: 'Completing',
    COMPLETING_FAILED: 'CompletingFailed',
    DECLINED: 'Declined',
    FAILED: 'Failed',
    INIT: 'Initializing',
    INIT_COMPLETE: 'Initialized',
    INIT_FAILED: 'InitializationFailed',
    SETTLED: 'Settled'
};

// eslint-disable-next-line no-control-regex
const containsNonPrintableAscii = (value) => value.match(/[^\x00-\x7F]/g);

const createThreeDSecureParameters = (getState) => {
    const currentState = getState();
    const { amountOwed: totalCost } = selectInvoiceCheckout(currentState);
    const memberFirstName = selectMemberFirstName(currentState);
    const firstName = containsNonPrintableAscii(memberFirstName)
        ? null
        : memberFirstName;
    const memberLastName = selectMemberLastName(currentState);
    const lastName = containsNonPrintableAscii(memberLastName)
        ? null
        : memberLastName;

    const buyerAddress = {
        countryCodeAlpha2: selectShippingRegionCountryCodeIso2(currentState),
        phoneNumber: selectSellerPhoneNumber(currentState)?.replace(
            /[^\d]/g,
            ''
        )
    };
    return {
        amount: totalCost,
        email: selectMemberEmail(currentState),
        billingAddress: {
            givenName: firstName,
            surname: lastName,
            ...buyerAddress
        },
        additionalInformation: {
            shippingGivenName: firstName,
            shippingSurname: lastName,
            shippingAddress: buyerAddress
        }
    };
};

export const initBraintreePayment =
    (paymentOption, onComplete) => async (dispatch, getState) => {
        let requestOptions = {};

        if (
            paymentOption === PAYMENT_OPTIONS.VISA_MASTERCARD ||
            paymentOption === PAYMENT_OPTIONS.GOOGLEPAY
        ) {
            requestOptions = {
                threeDSecure: createThreeDSecureParameters(getState)
            };
        }

        dispatch(setCheckoutSummaryFetching(true));

        try {
            if (paymentInstance) {
                const data =
                    await paymentInstance.requestPaymentMethod(requestOptions);
                dispatch(setPaymentMethodData(data));
            } else {
                throw { message: CHECKOUT_ERRORS.NO_PAYMENT_METHOD };
            }
            return dispatch(initPayment(onComplete));
        } catch (error) {
            return Promise.reject(error);
        }
    };

export const initPayment = (onComplete) => async (dispatch, getState) => {
    const currentState = getState();

    const { invoicePaymentAttemptId } =
        selectInvoiceOngoingPayment(currentState);
    const { selectedPayment, paymentMethodData } =
        selectInvoicePayment(currentState);
    const { invoiceId } = selectInvoiceCheckout(currentState);

    dispatch(closePaymentModal());
    dispatch(setCheckoutSummaryFetching(true));

    try {
        const paymentAttemptId = invoicePaymentAttemptId || utils.getUuid();

        if (!invoicePaymentAttemptId) {
            const formData = {
                invoicePaymentAttemptId: paymentAttemptId,
                invoiceId,
                paymentMethod: selectedPayment,
                paymentMethodData
            };
            await api.initializePayment(formData);
        }

        //POST successful? then start polling:
        return dispatch(
            checkPaymentStatus(paymentAttemptId, selectedPayment, onComplete)
        );
    } catch (error) {
        return Promise.reject(error);
    }
};

export const cancelPayment = async (invoicePaymentAttemptId) => {
    const formData = {
        invoicePaymentAttemptId
    };

    try {
        await api.cancelPayment(formData);
    } catch (error) {
        return Promise.reject(error);
    }
};

export const checkPaymentStatus =
    (paymentAttemptId, paymentMethod, onComplete) =>
    async (dispatch, getState) => {
        const { modal } = selectInvoicePayment(getState());

        let poll = true;

        try {
            const paymentStatus = await api.getPaymentStatus(paymentAttemptId);

            const { data } = paymentStatus;

            switch (data?.status) {
                case PAYMENT_STATUS.AUTHORIZED:
                case PAYMENT_STATUS.COMPLETING:
                case PAYMENT_STATUS.INIT:
                case PAYMENT_STATUS.INIT_COMPLETE:
                case PAYMENT_STATUS.CANCELLING:
                    poll = true;
                    break;
                case PAYMENT_STATUS.AWAITING_SWISH:
                    poll = true;
                    if (!modal.open) {
                        dispatch(
                            openPaymentModal({
                                open: MODAL_TYPE.SWISH_HANDEL,
                                paymentAttemptId
                            })
                        );
                    }
                    break;
                case PAYMENT_STATUS.SETTLED:
                    poll = false;
                    dispatch(closePaymentModal());
                    dispatch(setCheckoutSummaryFetching(false));
                    onComplete();
                    break;
                case PAYMENT_STATUS.DECLINED:
                    poll = false;
                    dispatch(closePaymentModal());
                    dispatch(resetOngoingPaymentAttempt());
                    dispatch(setCheckoutSummaryFetching(false));
                    return Promise.reject(new Error(CHECKOUT_ERRORS.DECLINED));
                case PAYMENT_STATUS.CANCELLED:
                    poll = false;
                    dispatch(closePaymentModal());
                    dispatch(resetOngoingPaymentAttempt());
                    dispatch(setCheckoutSummaryFetching(false));
                    break;
                case PAYMENT_STATUS.COMPLETING_FAILED:
                case PAYMENT_STATUS.FAILED:
                case PAYMENT_STATUS.INIT_FAILED:
                case PAYMENT_STATUS.CANCELLING_FAILED:
                    poll = false;
                    dispatch(closePaymentModal());
                    dispatch(resetOngoingPaymentAttempt());
                    dispatch(setCheckoutSummaryFetching(false));
                    return Promise.reject(
                        new Error(`Payment error: ${data?.status}`)
                    );
                default:
                    dispatch(closePaymentModal());
                    poll = false;
            }
        } catch (error) {
            poll = false;
            return Promise.reject(error);
        }

        if (poll) {
            await sleep(1500);
            return dispatch(
                checkPaymentStatus(paymentAttemptId, paymentMethod, onComplete)
            );
        }
    };
